import { z } from 'zod';
import { MAX_GST_RATE_BPS, resolveState } from '../tax.js';
import { checkBusinessIds } from './seller.js';
import { INVOICE_PREFIX_PATTERN } from '../invoice.js';

/**
 * What staff set in the admin: the store's GST details and the GST rate of each tax class, and the shipping charges
 * by order value. Money is in minor units (paise), rates in basis points (1800 = 18%).
 */

const rateBps = z.number().int().min(0, 'The rate cannot be below 0').max(MAX_GST_RATE_BPS, `GST goes up to ${MAX_GST_RATE_BPS / 100}%`);

export const taxSettingsSchema = z
  .object({
    /** where the store ships from: sales inside this state get CGST + SGST, sales to other states get IGST */
    sellerState: z.string().trim().min(1, 'Pick the state you ship from'),
    /** optional: shown on invoices */
    gstin: z.string().trim().toUpperCase().default(''),
    /** GST contained in the shipping charge (courier services are 18% GST); 0 if you do not charge GST on shipping */
    shippingRateBps: rateBps,
    // ---- what an invoice says about the seller; all optional, an invoice falls back to the store name ----
    /** the name the business is registered under ("ScrawlByte Solution"); the store name is used when empty */
    legalName: z.string().trim().max(120).default(''),
    /** what kind of business it is, as the invoice says: "Sole proprietorship", "LLP"... */
    businessType: z.string().trim().max(60).default(''),
    /** the PAN of the business: optional, printed on invoices */
    pan: z.string().trim().toUpperCase().default(''),
    /** the registered address, as it should read on an invoice (new lines are kept) */
    address: z.string().trim().max(400).default(''),
    contactEmail: z.string().trim().toLowerCase().max(254).refine((v) => v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'Enter a valid email address').default(''),
    contactPhone: z.string().trim().max(30).default(''),
    /** the start of every invoice number: "MS" gives MS/2026-27/00012 */
    invoicePrefix: z.string().trim().toUpperCase().regex(INVOICE_PREFIX_PATTERN, 'Use one or two letters or digits, like MS').default('MS'),
    /** a line printed at the foot of every invoice: returns, jurisdiction, thanks */
    invoiceNote: z.string().trim().max(400).default(''),
  })
  .superRefine((v, ctx) => checkBusinessIds({ gstin: v.gstin, pan: v.pan, state: v.sellerState }, ctx, 'sellerState'))
  .transform((v) => ({ ...v, sellerState: resolveState(v.sellerState)?.name ?? v.sellerState }));
export type TaxSettingsInput = z.infer<typeof taxSettingsSchema>;

/** A tax class is a named GST rate that products are put in ("standard" 18%, "reduced" 5%...). */
export const taxClassCreateSchema = z.object({
  name: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9_-]{1,39}$/, 'Use 2 to 40 lowercase letters, numbers, dashes or underscores'),
  rateBps,
});
export type TaxClassCreateInput = z.infer<typeof taxClassCreateSchema>;

export const taxClassUpdateSchema = z.object({ rateBps });

/** The shop-wide tax setup as the admin page shows it. */
export interface AdminTaxDTO {
  settings: TaxSettingsInput;
  classes: { id: string; name: string; rateBps: number; productCount: number }[];
}

// ---- shipping ----

/** One charge: orders below `upTo` (and from the previous band's `upTo`) pay `price`. The last band has no `upTo`. */
export const shippingBandSchema = z.object({
  upTo: z.number().int().min(1).max(1_000_000_000).nullable(),
  price: z.number().int().min(0, 'The charge cannot be below 0').max(10_000_000),
});

export const shippingServiceSchema = z
  .object({
    name: z.string().trim().min(1, 'Name this shipping option').max(60),
    estimatedDaysMin: z.number().int().min(1).max(90),
    estimatedDaysMax: z.number().int().min(1).max(90),
    /** only for parcels of at least this weight (grams) */
    minWeightGrams: z.number().int().min(0).max(1_000_000).default(0),
    /** and no heavier than this; null = no limit */
    maxWeightGrams: z.number().int().min(1).max(1_000_000).nullable().default(null),
    bands: z.array(shippingBandSchema).min(1, 'Add at least one charge').max(10, 'At most 10 charges per option'),
  })
  .superRefine((v, ctx) => {
    if (v.estimatedDaysMax < v.estimatedDaysMin) ctx.addIssue({ code: 'custom', path: ['estimatedDaysMax'], message: 'The latest delivery cannot be before the earliest' });
    if (v.maxWeightGrams != null && v.maxWeightGrams < v.minWeightGrams) ctx.addIssue({ code: 'custom', path: ['maxWeightGrams'], message: 'The heaviest cannot be below the lightest' });
    const last = v.bands.length - 1;
    v.bands.forEach((band, i) => {
      if (i < last && band.upTo == null) ctx.addIssue({ code: 'custom', path: ['bands', i, 'upTo'], message: 'Only the last charge can have no upper limit' });
      if (i === last && band.upTo != null) ctx.addIssue({ code: 'custom', path: ['bands', i, 'upTo'], message: 'The last charge must cover every larger order' });
      const before = v.bands[i - 1]?.upTo;
      if (i > 0 && band.upTo != null && before != null && band.upTo <= before) {
        ctx.addIssue({ code: 'custom', path: ['bands', i, 'upTo'], message: 'Each limit must be higher than the one before' });
      }
    });
  });
export type ShippingServiceInput = z.infer<typeof shippingServiceSchema>;

export const shippingSettingsSchema = z
  .object({ services: z.array(shippingServiceSchema).min(1, 'Add at least one shipping option').max(8, 'At most 8 shipping options') })
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.services.forEach((s, i) => {
      const key = s.name.toLowerCase();
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: ['services', i, 'name'], message: 'Two shipping options have the same name' });
      seen.add(key);
    });
  });
export type ShippingSettingsInput = z.infer<typeof shippingSettingsSchema>;

/** What customers need to know about the store's tax and shipping rules, from the public API. */
export interface CommerceInfoDTO {
  sellerState: string;
  /** orders at or above this get free shipping (the lowest such amount over all options), or null */
  freeShippingOver: { amount: number; currency: string } | null;
  /** what paying online takes off an order (for orders of at least minSubtotal), or null when there is no such offer */
  onlineOffer: { amount: number; minSubtotal: number; currency: string } | null;
}
